import React, {useEffect, useMemo, useRef, useState} from "react";
import {InputFileAcceptTypes} from "../../../../../core/constants/enums";
import {createUUId, deepCopy} from "../../../../../core/services/utils/utils";
import useIsMounted from "../../../../hooks/use-is-mounted";
import {readString} from "react-papaparse";
import DropZone from "../../../base/dropzone";
import {ReactComponent as ImagePlaceholder} from "../../../../../assets/images/dropzone/upload-placeholder.svg";

const NewPackingListDropZone = ({
                                    accept = [InputFileAcceptTypes.csv],
                                    onParsed,
                                }) => {
    const idRef = useRef(createUUId(true));
    const [parseError, setParseError] = useState(null);
    const [parsing, setParsing] = useState(false);
    const [selectedFileName, setSelectedFileName] = useState(null);
    const isMounted = useIsMounted();

    /**
     * Memo version of minimum number of columns needed in the file
     * @type {number}
     */
    const requiredColumnLength = useMemo(() => 2, [])

    /**
     * With each change in the parseError state:
     * - if set, removes the error after 2 second timeout.
     */
    useEffect(() => {
        if (!parseError)
            return;
        const timer = setTimeout(() => {
            setParseError(null);
        }, 2000)
        return () => clearTimeout(timer);
    }, [parseError])

    /**
     * Validates and parses the provided csv file.
     *
     * * if the file is parsed properly, calls the onParsed callback with the parsed file as its arg.
     * @param files {File[] | null}
     * @param fileName {string}
     */
    const onFileSelected = (files, fileName) => {
        setParseError(null);
        const file = files[0];
        setParsing(true);
        const parsed = validateAndParseSelectedFile(file);
        if (!isMounted())
            return;
        setParsing(false);
        if (parsed) {
            setSelectedFileName(fileName);
            onParsed(parsed);
        }
    }

    /**
     * Validates the imported file
     * @param file
     */
    const validateAndParseSelectedFile = (file) => {
        try {
            const parsed = readString(file, {header: false, skipEmptyLines: true});
            let columns = Array.from((parsed?.data ? parsed?.data[0] : []) ?? [])
            let rows = deepCopy(parsed?.data?.slice(1) ?? []);
            columns = columns?.filter((e, i) => {
                const retain = !!e;
                if (!retain)
                    rows.splice(i, 1);
                return retain;
            });

            // validate
            if (columns?.length < requiredColumnLength)
                return setParseError("The chosen CSV file does not contain minimum required columns");
            if (rows?.length < 1)
                return setParseError('The chosen CSV file does not contain any values.');
            // parse the data frame
            return parseImportedData(columns, rows);
        } catch (e) {
            setParseError('Error while parsing the provided CSV file.');
        }
    }

    /**
     * Parses the imported data based on the received columns and rows and returns list of objects
     * @param columns
     * @param rows
     * @return {object[]}
     */
    const parseImportedData = (columns, rows) => {
        return rows;
    }

    return (
        <>
            <DropZone
                disabled={parsing}
                identifier={'new-packing-list-drop-zone'}
                accept={accept}
                id={idRef.current}
                returnFileName={true}
                setLoading={() => (void 0)}
                onSuccess={onFileSelected}
                error={!!parseError}
                multiple={false}
                openOnClick
            >
                {() =>
                    <div className={'new-packing-list-drop-zone-content'}>
                        <p className={'placeholder-text'}>
                            Upload your csv file here by clicking or dragging and dropping.
                        </p>
                        <ImagePlaceholder className={'placeholder-image'}/>
                        {
                            parseError
                                ? <p>
                                    {parseError}
                                </p>
                                : !!selectedFileName
                                    ? <p className={'selected-file'}>
                                        {selectedFileName}
                                    </p>
                                    : <></>
                        }
                    </div>
                }
            </DropZone>
        </>
    );
}

export default NewPackingListDropZone;
